import { useEffect, useMemo, useState } from "react";
import { api, money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Hourglass, Clock, CheckCircle2, Percent, Coins, CalendarDays, Download, FileText } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

function buildPresets() {
  const now = new Date();
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return [
    { key: "today", label: "Today", range: () => [iso(now), iso(now)] },
    { key: "yesterday", label: "Yesterday", range: () => { const y = addDays(now, -1); return [iso(y), iso(y)]; } },
    { key: "7d", label: "Last 7 days", range: () => [iso(addDays(now, -6)), iso(now)] },
    { key: "30d", label: "Last 30 days", range: () => [iso(addDays(now, -29)), iso(now)] },
    { key: "this_month", label: "This month", range: () => [iso(startOfMonth(now)), iso(now)] },
    { key: "last_month", label: "Last month", range: () => [iso(startOfMonth(lastMonthDate)), iso(endOfMonth(lastMonthDate))] },
  ];
}

const fmt = (s) => new Date(s + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export function DashboardCharts({ theme = "light", showExport = false }) {
  const dark = theme === "dark";
  const presets = useMemo(buildPresets, []);
  const [preset, setPreset] = useState("7d");
  const [from, setFrom] = useState(presets[2].range()[0]);
  const [to, setTo] = useState(presets[2].range()[1]);
  const [tmpFrom, setTmpFrom] = useState(from);
  const [tmpTo, setTmpTo] = useState(to);
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get(`/dashboard/summary?date_from=${from}&date_to=${to}`).then((r) => setData(r.data)).catch(() => {});
  }, [from, to]);

  const applyPreset = (p) => {
    const [f, t] = p.range();
    setPreset(p.key); setFrom(f); setTo(t); setTmpFrom(f); setTmpTo(t); setOpen(false);
  };
  const applyCustom = () => {
    if (!tmpFrom || !tmpTo || tmpFrom > tmpTo) return;
    setPreset("custom"); setFrom(tmpFrom); setTo(tmpTo); setOpen(false);
  };

  const activeLabel = preset === "custom"
    ? `${fmt(from)} – ${fmt(to)}`
    : (presets.find((p) => p.key === preset)?.label || `${fmt(from)} – ${fmt(to)}`);

  const cardCls = dark ? "rounded-xl border border-zinc-800 bg-[#18181B] p-4" : "rounded-xl border border-slate-200 bg-white shadow-sm p-4";
  const label = dark ? "text-zinc-400" : "text-slate-400";
  const val = dark ? "text-white" : "text-slate-900";
  const inputCls = dark ? "bg-[#0A0A0A] border-zinc-700 text-white" : "bg-white border-slate-300 text-slate-900";

  const successRate = (() => {
    const rq = data?.cards?.requested?.count || 0;
    const sc = data?.cards?.successful?.count || 0;
    if (!rq) return 0;
    return Math.round((sc / rq) * 1000) / 10;
  })();

  const cards = [
    { key: "requested", label: "Total Requested", icon: Hourglass, accent: "text-sky-500" },
    { key: "pending", label: "Total Pending", icon: Clock, accent: "text-amber-500" },
    { key: "successful", label: "Total Successful", icon: CheckCircle2, accent: "text-emerald-500" },
  ];

  const exportCSV = () => {
    if (!data) return;
    const lines = [];
    lines.push(["ABCD Pay PayIn Report"]);
    lines.push([`Date range,${from} to ${to}`]);
    lines.push([]);
    lines.push(["Metric", "Transactions", "Amount"]);
    lines.push(["Requested", data.cards.requested.count, data.cards.requested.sum]);
    lines.push(["Pending", data.cards.pending.count, data.cards.pending.sum]);
    lines.push(["Successful", data.cards.successful.count, data.cards.successful.sum]);
    lines.push(["Success Rate (%)", successRate, ""]);
    lines.push([]);
    lines.push(["Date", "Requested", "Pending", "Successful"]);
    (data.trend || []).forEach((t) => lines.push([t.date, t.requested, t.pending, t.successful]));
    const esc = (c) => { const s = String(c ?? ""); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const csv = lines.map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `abcdpay-payin_${from}_to_${to}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const exportPDF = () => {
    if (!data) return;
    const doc = new jsPDF();
    doc.setFontSize(16); doc.setTextColor(16, 185, 129);
    doc.text("ABCD Pay — PayIn Report", 14, 18);
    doc.setFontSize(10); doc.setTextColor(100);
    doc.text(`Date range: ${fmt(from)} – ${fmt(to)}`, 14, 25);
    autoTable(doc, {
      startY: 32,
      head: [["Metric", "Transactions", "Amount"]],
      body: [
        ["Requested", data.cards.requested.count, money(data.cards.requested.sum)],
        ["Pending", data.cards.pending.count, money(data.cards.pending.sum)],
        ["Successful", data.cards.successful.count, money(data.cards.successful.sum)],
        ["Success Rate", `${successRate}%`, ""],
      ],
      headStyles: { fillColor: [16, 185, 129] },
      styles: { fontSize: 9 },
    });
    autoTable(doc, {
      startY: (doc.lastAutoTable?.finalY || 60) + 8,
      head: [["Date", "Requested", "Pending", "Successful"]],
      body: (data.trend || []).map((t) => [t.date, money(t.requested), money(t.pending), money(t.successful)]),
      headStyles: { fillColor: [30, 41, 59] },
      styles: { fontSize: 8 },
    });
    doc.save(`abcdpay-payin_${from}_to_${to}.pdf`);
  };

  return (
    <div data-testid="role-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" data-testid="dashboard-range-trigger"
              className={dark ? "bg-[#0A0A0A] border-zinc-700 text-white hover:bg-zinc-900 hover:text-white gap-2" : "gap-2"}>
              <CalendarDays className="w-4 h-4" />
              {activeLabel}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-auto p-0 flex" data-testid="dashboard-range-popover">
            <div className="flex flex-col p-2 border-r min-w-[150px]">
              {presets.map((p) => (
                <button key={p.key} data-testid={`dashboard-preset-${p.key}`} onClick={() => applyPreset(p)}
                  className={`text-left text-sm px-3 py-2 rounded-md transition-colors ${preset === p.key ? "bg-emerald-500 text-white" : "hover:bg-slate-100"}`}>
                  {p.label}
                </button>
              ))}
            </div>
            <div className="p-3 flex flex-col gap-2">
              <div>
                <label className="text-xs text-slate-500 block mb-1">From</label>
                <Input type="date" data-testid="dashboard-from" value={tmpFrom} max={tmpTo} onChange={(e) => setTmpFrom(e.target.value)} className="w-44" />
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">To</label>
                <Input type="date" data-testid="dashboard-to" value={tmpTo} min={tmpFrom} max={iso(new Date())} onChange={(e) => setTmpTo(e.target.value)} className="w-44" />
              </div>
              <Button size="sm" onClick={applyCustom} data-testid="dashboard-apply-custom" className="bg-emerald-600 hover:bg-emerald-700 text-white mt-1">Apply</Button>
            </div>
          </PopoverContent>
        </Popover>

        {showExport && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={exportCSV} data-testid="dashboard-export-csv" className="gap-2">
              <Download className="w-4 h-4" /> CSV
            </Button>
            <Button variant="outline" size="sm" onClick={exportPDF} data-testid="dashboard-export-pdf" className="gap-2">
              <FileText className="w-4 h-4" /> PDF
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {cards.map((c) => {
          const Icon = c.icon;
          const d = data?.cards?.[c.key];
          return (
            <div key={c.key} data-testid={`dashboard-card-${c.key}`} className={cardCls}>
              <div className="flex items-center justify-between">
                <Icon className={`w-4 h-4 ${c.accent}`} />
                <span className={`text-xs ${label}`} data-testid={`dashboard-count-${c.key}`}>{d ? d.count : 0} txns</span>
              </div>
              <div className={`text-2xl font-black font-mono mt-2 ${val}`} data-testid={`dashboard-sum-${c.key}`}>{d ? money(d.sum) : "—"}</div>
              <div className={`text-[11px] uppercase tracking-wider font-semibold mt-0.5 ${label}`}>{c.label}</div>
            </div>
          );
        })}
        <div data-testid="dashboard-card-success-rate" className={cardCls}>
          <div className="flex items-center justify-between">
            <Percent className="w-4 h-4 text-violet-500" />
            <span className={`text-xs ${label}`}>{data?.cards?.successful?.count || 0}/{data?.cards?.requested?.count || 0}</span>
          </div>
          <div className={`text-2xl font-black font-mono mt-2 ${val}`} data-testid="dashboard-success-rate">{data ? `${successRate}%` : "—"}</div>
          <div className={`text-[11px] uppercase tracking-wider font-semibold mt-0.5 ${label}`}>Success Rate</div>
        </div>
        <div data-testid="dashboard-card-commission" className={cardCls}>
          <div className="flex items-center justify-between">
            <Coins className="w-4 h-4 text-amber-500" />
            <span className={`text-xs ${label}`}>In {data ? money(data.cards?.commission?.payin || 0) : "—"} · Out {data ? money(data.cards?.commission?.payout || 0) : "—"}</span>
          </div>
          <div className={`text-2xl font-black font-mono mt-2 ${val}`} data-testid="dashboard-commission-total">{data ? money(data.cards?.commission?.total || 0) : "—"}</div>
          <div className={`text-[11px] uppercase tracking-wider font-semibold mt-0.5 ${label}`}>Total Commission</div>
        </div>
      </div>

      <div className={`${cardCls} mt-4`}>
        <div className={`text-sm font-semibold mb-3 ${val}`}>Daily Trend</div>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={data?.trend || []}>
            <CartesianGrid strokeDasharray="3 3" stroke={dark ? "#27272a" : "#e2e8f0"} />
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: dark ? "#a1a1aa" : "#64748b" }} tickFormatter={(d) => (d || "").slice(5)} />
            <YAxis tick={{ fontSize: 11, fill: dark ? "#a1a1aa" : "#64748b" }} />
            <Tooltip formatter={(v) => money(v)} contentStyle={dark ? { background: "#18181B", border: "1px solid #3f3f46", color: "#fff" } : undefined} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="requested" name="Requested" fill="#0ea5e9" radius={[3, 3, 0, 0]} />
            <Bar dataKey="pending" name="Pending" fill="#f59e0b" radius={[3, 3, 0, 0]} />
            <Bar dataKey="successful" name="Successful" fill="#10b981" radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
